// Client/src/pages/digi/NewMasterSheet/rowExtras.js
//
// The computed mastersheet columns (spec 2026-10-14): what the row-extras endpoint returns,
// how each cell reads, and the six filters over it. Pure — no React, no fetch.

export const COMPUTED_PREFIX = "__x_";
export const isComputedKey = (key) => String(key || "").startsWith(COMPUTED_PREFIX);

/** key → label, the "More columns" group it folds under, and the stored column it sits after. */
export const COMPUTED_COLUMNS = [
  { key: "__x_leaseKind", label: "Lease kind", group: "term", after: "originalLeaseStart" },
  { key: "__x_totalDeposit", label: "Total SD", group: "deposits", after: "psfNonRefundable" },
  { key: "__x_insurance", label: "Insurance", group: "insurance", after: "insuranceExpiry" },
  { key: "__x_addendums", label: "Addendums", group: "leaseExtras", after: null },
  { key: "__x_incentives", label: "Incentives (PAD)", group: "leaseExtras", after: null },
  { key: "__x_padThisMonth", label: "PAD this month", group: "leaseExtras", after: null },
  { key: "__x_futureLease", label: "Future lease", group: "leaseExtras", after: null },
  { key: "__x_aiSuggestion", label: "AI suggestion", group: "leasePlanning", after: "tenantRating" },
];

const money = (n) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const shortDate = (v) => {
  if (!v) return "";
  const d = new Date(`${String(v).slice(0, 10)}T12:00:00Z`);
  if (!Number.isFinite(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
};

/** tone: ok | soon | bad | none | muted — the pill colour; text: what it says. */
export function insuranceLabel(x) {
  if (!x || x.status === "VACANT") return { tone: "muted", text: "—" };
  if (x.status === "NO_POLICY") return { tone: "none", text: "No policy uploaded" };
  if (x.status === "NO_DATE") return { tone: "muted", text: "No expiry date" };
  const when = shortDate(x.expiry);
  if (x.status === "VALID") return { tone: "ok", text: `Valid · ${when}` };
  if (x.status === "EXPIRING") return { tone: "soon", text: `Expiring · ${when}` };
  return { tone: "bad", text: `Expired · ${when}` };
}

export function padThisMonthLabel(x) {
  if (!x || x.state === "VACANT") return "—";
  if (x.state === "STOPPED") return "Stopped";
  if (x.state === "NO_PAD") return "No PAD";
  return x.amount === null || x.amount === undefined ? "—" : money(x.amount);
}

export function futureLeaseLabel(x) {
  if (!x) return "—";
  const kind = x.kind === "renewal" ? "Renewal" : "New";
  if (x.signed) return `${kind} · signed · ${shortDate(x.start)}`;
  return `${kind} · ${x.name} · awaiting signatures`;
}

export const totalDepositLabel = (x) => (x && x.total !== null && x.total !== undefined ? money(x.total) : "—");
export const addendumsLabel = (x) => (x && x.current ? `Yes (${x.count})` : "No");
export const incentivesLabel = (x) => (x && x.yes ? "Yes" : "No");

/** The six filters: key, label, options, the group to open, and the predicate over (row, extras). */
export const EXTRA_FILTERS = [
  {
    key: "affordable", label: "Affordable", group: "unitDetails",
    options: [{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }],
    match: (row, _x, v) => String(row?.affordableUnit || "No").trim().toLowerCase() === v.toLowerCase(),
  },
  {
    key: "insurance", label: "Insurance", group: "insurance",
    options: [
      { value: "VALID", label: "Valid" }, { value: "EXPIRING", label: "Expiring" },
      { value: "EXPIRED", label: "Expired" }, { value: "NO_POLICY", label: "No policy uploaded" },
    ],
    match: (_r, x, v) => !!x && x.insurance?.status === v,
  },
  {
    key: "addendums", label: "Addendums", group: "leaseExtras",
    options: [{ value: "Yes", label: "Has current addendum" }, { value: "No", label: "None" }],
    match: (_r, x, v) => !!x && (x.addendums?.current ? "Yes" : "No") === v,
  },
  {
    key: "incentives", label: "Incentives", group: "leaseExtras",
    options: [{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }],
    match: (_r, x, v) => !!x && (x.incentives?.yes ? "Yes" : "No") === v,
  },
  {
    key: "futureLease", label: "Future lease", group: "leaseExtras",
    options: [
      { value: "RENEWAL_SIGNED", label: "Renewal signed" }, { value: "NEW_SIGNED", label: "New lease signed" },
      { value: "AWAITING", label: "Awaiting signatures" }, { value: "NONE", label: "None" },
    ],
    match: (_r, x, v) => {
      if (!x) return false;
      const f = x.futureLease;
      if (v === "NONE") return !f;
      if (!f) return false;
      if (v === "AWAITING") return !f.signed;
      return f.signed && (v === "RENEWAL_SIGNED" ? f.kind === "renewal" : f.kind === "new");
    },
  },
  {
    key: "leaseKind", label: "Lease kind", group: "term",
    options: [{ value: "Renewal", label: "Renewal" }, { value: "New", label: "New lease" }],
    match: (_r, x, v) => !!x && x.leaseKind === v,
  },
];

export const groupForFilter = (key) => EXTRA_FILTERS.find((f) => f.key === key)?.group || null;

/** True when the row passes every set filter. `extras` may be undefined while loading. */
export function matchesExtraFilters(row, extras, filters) {
  for (const f of EXTRA_FILTERS) {
    const v = filters?.[f.key];
    if (v === undefined || v === null || v === "") continue;
    if (!f.match(row, extras, String(v))) return false;
  }
  return true;
}

export const hasExtraFilters = (filters) => EXTRA_FILTERS.some((f) => filters?.[f.key]);
